/**
 * File-import jobs (CSV/JSON) — hand-written against client.instance, backed by
 * POST/GET /contacts/import/jobs (datahub-api ContactImportJobEndpoints). Live
 * progress arrives over the shared SignalR jobs hub (src/realtime) as jobType
 * "import"; the GET endpoints are the seed + polling fallback. Mirrors bulkJobs.ts.
 */

import { useMutation } from '@tanstack/react-query';

import { useBulkJobsStore, type BulkJobStatus } from '@/realtime/bulkJobsStore';

import { client } from '../generated/client.gen';

export type ImportFormat = 'standard' | 'ecomail' | 'simple' | 'profile' | 'json';

export interface ImportJobDto {
  id: string;
  format: string;
  source: string;
  fileName?: string | null;
  status: BulkJobStatus;
  total: number;
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: { id: string; error: string }[];
  error?: string | null;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
}

export interface StartImportJobInput {
  file: File;
  format: ImportFormat;
  source: string;
}

function useStartImportJob() {
  const upsert = useBulkJobsStore((s) => s.upsert);
  return useMutation({
    mutationFn: async (input: StartImportJobInput): Promise<ImportJobDto> => {
      const fd = new FormData();
      fd.append('file', input.file);
      fd.append('format', input.format);
      fd.append('source', input.source);
      const res = await client.instance.post<ImportJobDto>('/contacts/import/jobs', fd);
      return res.data;
    },
    onSuccess: (job) => {
      // Seed the store so the progress dialog has data before the first
      // SignalR push arrives.
      upsert({
        id: job.id,
        jobType: 'import',
        operation: job.format,
        status: job.status,
        total: job.total,
        processed: job.processed,
        affected: job.created + job.updated,
        skipped: job.skipped,
        created: job.created,
        updated: job.updated,
        failed: job.errors?.length ?? 0,
        error: job.error,
      });
    },
  });
}

export async function fetchRecentImportJobs(limit = 10): Promise<ImportJobDto[]> {
  const res = await client.instance.get<ImportJobDto[]>('/contacts/import/jobs', {
    params: { limit },
  });
  return res.data;
}

export const importJobsApi = {
  useStartImportJob,
};
